import { useEffect, useRef } from 'react';

/**
 * Reveals `.vxm-reveal` descendants as they scroll into view.
 *
 * Returns a ref to attach to a container; every `.vxm-reveal` inside it is
 * observed once and given `.is-visible` on entry.
 *
 * Two deliberate fallbacks, because an element that never receives the class
 * stays at opacity 0 and the page would appear blank:
 *   - reduced-motion users and browsers without IntersectionObserver get the
 *     class applied immediately;
 *   - each element is unobserved after revealing, so nothing re-hides on
 *     scroll-up and the observer sheds work as the user moves down the page.
 */
export function useReveal() {
  const containerRef = useRef(null);

  useEffect(() => {
    const root = containerRef.current;
    if (!root) return undefined;

    const targets = root.querySelectorAll('.vxm-reveal');
    if (!targets.length) return undefined;

    const prefersReducedMotion =
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (prefersReducedMotion || typeof IntersectionObserver === 'undefined') {
      targets.forEach((el) => el.classList.add('is-visible'));
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          }
        });
      },
      // Negative bottom margin holds the reveal until the element is a little
      // way into the viewport, which reads as intentional rather than twitchy.
      { threshold: 0.1, rootMargin: '0px 0px -40px 0px' }
    );

    targets.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return containerRef;
}

export default useReveal;
